/**
 * WASM bridge — loads valko-wasm module with automatic JS fallback.
 *
 * Usage:
 *   import { initWasm, filterSessions, searchMessages, estimateCost, splitTtsChunks } from './wasm/bridge';
 *   await initWasm(); // call once at app startup
 *
 * All functions fall back to JS implementations if WASM fails to load.
 * The fallback is silent — no errors thrown.
 */

import type { SessionSummary } from '@voice-assistant/shared';

// ── WASM module (dynamically loaded) ────────────────────────────────────────
//
// The wasm-pkg directory is generated by CI (wasm-pack → uploaded artifact) and
// is NOT committed to the repo. Using a static `typeof import(...)` or a plain
// dynamic import would make Rollup try to resolve the module at build time and
// fail with "Could not resolve ../wasm-pkg/valko_wasm". We intentionally avoid
// both: type is `any`, and the dynamic import is marked `@vite-ignore` so Vite
// leaves it as a runtime dynamic import (which gracefully 404s → JS fallback
// when wasm-pkg is absent, as is the case for self-hosted prod builds).

type WasmModule = any;
let wasm: WasmModule | null = null;

/** Returns true if WASM was loaded successfully. */
export async function initWasm(): Promise<boolean> {
  // Opt-out: ?wasm=0 or localStorage flag disables WASM
  const params = new URLSearchParams(location.search);
  if (params.get('wasm') === '0') return false;

  // Opt-in: ?wasm=1 or settings store sets flag for this browser.
  // settings-zustand-migration Phase 2: legacy `valko-wasm-enabled` localStorage
  // is migrated into the store's `wasmEnabled` slice. `?wasm=1` still sets it.
  const forceEnable = params.get('wasm') === '1';
  // Lazy import keeps `initWasm` callable outside React without circular deps.
  const { useSettingsStore } = await import('../state/settings.store');
  if (forceEnable) useSettingsStore.getState().setWasmEnabled(true);

  const enabled = forceEnable || useSettingsStore.getState().wasmEnabled;
  if (!enabled) return false;

  try {
    // Variable indirection + @vite-ignore — Rollup/Vite cannot statically
    // resolve a non-literal dynamic-import specifier, which is what we want
    // when `wasm-pkg/` is absent from the build context (self-hosted prod).
    const wasmSpecifier = '../wasm-pkg/valko_wasm';
    const mod = await import(/* @vite-ignore */ wasmSpecifier);
    await mod.default(); // initialise WASM memory
    wasm = mod;
    console.debug('[valko-wasm] loaded successfully');
    return true;
  } catch (e) {
    console.debug('[valko-wasm] load failed, using JS fallback:', e);
    return false;
  }
}

export function isWasmEnabled(): boolean {
  return wasm !== null;
}

// ── Session filtering ────────────────────────────────────────────────────────

/** Filter and sort sessions by query. Pinned sessions always appear first. */
export function filterSessions(sessions: SessionSummary[], query: string): SessionSummary[] {
  if (wasm) {
    try {
      return JSON.parse(wasm.filter_sessions(JSON.stringify(sessions), query)) as SessionSummary[];
    } catch {
      // Fall through to JS
    }
  }
  return jsFallbackFilter(sessions, query);
}

function jsFallbackFilter(sessions: SessionSummary[], query: string): SessionSummary[] {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? sessions.filter((s) => {
        return (
          s.name?.toLowerCase().includes(q) ||
          s.previewText?.toLowerCase().includes(q) ||
          s.contextTag?.toLowerCase().includes(q) ||
          s.rollingSignpost?.toLowerCase().includes(q) ||
          s.model?.toLowerCase().includes(q)
        );
      })
    : [...sessions];

  return filtered.sort((a, b) => {
    const pa = (a as any).listPinPosition ?? null;
    const pb = (b as any).listPinPosition ?? null;
    if (pa !== null && pb !== null) return pa - pb;
    if (pa !== null) return -1;
    if (pb !== null) return 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

// ── Fuzzy search ─────────────────────────────────────────────────────────────

export interface MessageSearchResult {
  id: string;
  role: string;
  content: string;
  createdAt?: string;
  sessionId?: string;
  score: number;
  indices: [number, number][];
}

export interface SearchableMessage {
  id: string;
  role: string;
  content: string;
  createdAt?: string;
  sessionId?: string;
}

/** Fuzzy search across messages using skim/fzf algorithm. Returns ranked results with match positions. */
export function searchMessages(messages: SearchableMessage[], query: string, limit = 50): MessageSearchResult[] {
  if (wasm) {
    try {
      return JSON.parse(wasm.search_messages(JSON.stringify(messages), query, limit)) as MessageSearchResult[];
    } catch {
      // Fall through to JS
    }
  }
  return jsFallbackSearch(messages, query, limit);
}

function jsFallbackSearch(messages: SearchableMessage[], query: string, limit: number): MessageSearchResult[] {
  if (!query.trim()) return [];
  const q = query.toLowerCase();
  return messages
    .filter((m) => m.content.toLowerCase().includes(q))
    .slice(0, limit)
    .map((m) => ({
      ...m,
      score: 0,
      indices: [],
    }));
}

// ── Cost calculation ─────────────────────────────────────────────────────────

/** Calculate cost in USD. Direct scalar call — no JSON serialization. */
export function estimateCost(modelId: string, inputTokens: number, outputTokens: number): number {
  if (wasm) {
    try {
      return wasm.estimate_cost(modelId, inputTokens, outputTokens);
    } catch {
      // Fall through
    }
  }
  return jsFallbackCost(modelId, inputTokens, outputTokens);
}

function jsFallbackCost(modelId: string, input: number, output: number): number {
  const pricing: Record<string, [number, number]> = {
    'claude-sonnet-4': [3.0, 15.0],
    'claude-haiku-4': [0.8, 4.0],
    'claude-opus-4': [15.0, 75.0],
    'gemini-2.0-flash': [0.1, 0.4],
    'gemini-1.5-pro': [1.25, 5.0],
  };
  const key = Object.keys(pricing).find((k) => modelId.includes(k));
  if (!key) return 0;
  const [inPer1m, outPer1m] = pricing[key];
  return (input * inPer1m + output * outPer1m) / 1_000_000;
}

// ── TTS text chunker ─────────────────────────────────────────────────────────

/** Split text into TTS-appropriate chunks. */
export function splitTtsChunks(text: string): string[] {
  if (wasm) {
    try {
      return JSON.parse(wasm.split_tts_chunks(text)) as string[];
    } catch {
      // Fall through
    }
  }
  return jsFallbackChunker(text);
}

function jsFallbackChunker(text: string): string[] {
  // Simple fallback: split on sentence-ending punctuation
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 2);
}
